import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { useI18n } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static fallback used while the Places API is not yet configured.
// Chef can update these two numbers in /app/frontend/src/components/GoogleReviewsBadge.jsx
const FALLBACK = { rating: 5.0, total: null };

export default function GoogleReviewsBadge() {
  const { t } = useI18n();
  const g = t.google || {};
  const [data, setData] = useState({ rating: FALLBACK.rating, total: FALLBACK.total, write_review_url: null, loading: true });

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/google/reviews`)
      .then((r) => {
        if (!active) return;
        setData({
          rating: r.data.rating ?? FALLBACK.rating,
          total: r.data.total ?? FALLBACK.total,
          write_review_url: r.data.write_review_url || null,
          loading: false,
        });
      })
      .catch(() => active && setData((d) => ({ ...d, loading: false })));
    return () => { active = false; };
  }, []);

  const stars = Math.round(data.rating || 0);
  const ratingDisplay = (data.rating || FALLBACK.rating).toFixed(1);
  const writeUrl = data.write_review_url || "https://search.google.com/local/writereview?placeid=ChIJ1eeCNOFhLxMRE_9HNt3eALY";

  return (
    <section className="bg-[#FAF9F6] py-16 sm:py-20" data-testid="google-reviews-badge">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="relative bg-[#1F1A14] text-[#FAF9F6] rounded-sm px-6 py-10 sm:px-10 sm:py-14 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-56 h-56 bg-[#C85A3C]/10 rounded-full blur-3xl" aria-hidden="true" />
          <div className="relative grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-center">
            {/* G logo */}
            <div className="shrink-0 flex items-center justify-center bg-white rounded-full w-16 h-16 shadow-md" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z"/>
              </svg>
            </div>

            {/* Rating */}
            <div>
              <p className="overline text-[#D4B98D]">{g.overline || "Google Reviews"}</p>
              <div className="mt-2 flex items-center flex-wrap gap-x-4 gap-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-serif-display text-4xl sm:text-5xl text-[#FAF9F6]" data-testid="google-rating-value">
                    {ratingDisplay}
                  </span>
                  <span className="text-[#FAF9F6]/50 text-sm">/ 5</span>
                </div>
                <div className="flex items-center gap-1" aria-label={`${ratingDisplay} out of 5`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5"
                      strokeWidth={1}
                      fill={i < stars ? "#F0D9A7" : "transparent"}
                      stroke={i < stars ? "#F0D9A7" : "#FAF9F6"}
                    />
                  ))}
                </div>
              </div>
              <p className="mt-2 text-sm text-[#FAF9F6]/70" data-testid="google-rating-total">
                {data.total != null
                  ? (g.basedOn || "Based on {{n}} reviews").replace("{{n}}", data.total)
                  : (g.verifiedOnGoogle || "Verified on Google")}
              </p>
            </div>

            {/* CTA */}
            <a
              href={writeUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="google-write-review-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C85A3C] hover:bg-[#B24A2E] text-white text-sm tracking-widest uppercase px-6 py-3.5 rounded-sm transition whitespace-nowrap"
            >
              {g.writeReview || "Write a review"} <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
